import React, { useEffect, useState } from 'react';
import { RiCheckLine, RiCloseLine, RiErrorWarningLine } from 'react-icons/ri';

const CONFIG = {
  success: {
    bg:     '#f0fdf4',
    border: '#86efac',
    icon:   '#16a34a',
    text:   '#15803d',
    Icon:   RiCheckLine,
    dot:    '#22c55e',
  },
  error: {
    bg:     '#fef2f2',
    border: '#fca5a5',
    icon:   '#dc2626',
    text:   '#b91c1c',
    Icon:   RiErrorWarningLine,
    dot:    '#ef4444',
  },
  info: {
    bg:     'rgba(9,132,227,0.06)',
    border: 'rgba(9,132,227,0.25)',
    icon:   '#0984e3',
    text:   '#0984e3',
    Icon:   RiCheckLine,
    dot:    '#0984e3',
  },
};

export default function Toast({ message, type = 'success', onClose }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Trigger slide-in on mount
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  const cfg = CONFIG[type] || CONFIG.info;
  const { Icon } = cfg;

  return (
    <div
      className="fixed top-5 right-5 z-[9999] flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.12)] border transition-all duration-300"
      style={{
        background:  cfg.bg,
        borderColor: cfg.border,
        minWidth:    '280px',
        maxWidth:    '380px',
        transform:   visible ? 'translateX(0) scale(1)' : 'translateX(110%) scale(0.95)',
        opacity:     visible ? 1 : 0,
      }}
    >
      {/* Icon circle */}
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: cfg.dot + '22', border: `1.5px solid ${cfg.dot}55` }}
      >
        <Icon size={14} style={{ color: cfg.icon }} />
      </div>

      {/* Message */}
      <p className="text-sm font-semibold flex-1 leading-snug" style={{ color: cfg.text }}>
        {message}
      </p>

      {/* Close */}
      <button
        onClick={onClose}
        className="flex-shrink-0 cursor-pointer opacity-40 hover:opacity-100 transition-opacity"
        style={{ color: cfg.text }}
        aria-label="Dismiss"
      >
        <RiCloseLine size={16} />
      </button>
    </div>
  );
}

/**
 * useToast — returns { toast, showToast }
 * showToast(message, type?)  type: 'success' | 'error' | 'info'
 * Render <ToastHost toast={toast} clearToast={clearToast} /> in your JSX.
 */
export function useToast(duration = 3200) {
  const [toast, setToast] = useState(null); // { message, type } | null

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), duration);
  };

  const clearToast = () => setToast(null);

  return { toast, showToast, clearToast };
}

export function ToastHost({ toast, clearToast }) {
  if (!toast) return null;
  return <Toast message={toast.message} type={toast.type} onClose={clearToast} />;
}
